import React, {useMemo, useState} from "react";
import "./style.css";
import {useSelector, useDispatch} from "react-redux";
import {toast} from "react-toastify";
import Swal from "sweetalert2";
import {useCreateOrderMutation} from "../../redux/service/paymntService";
import {downgradeToFree} from "../../redux/userSlice";
import AuthModalRHF from "../../Components/AuthModalRHF";
import {useEffect} from "react";
import {sinhalaData, englishData, tamilData} from "../../dummyData/SubscribeDummyData";

function splitName(fullName) {
    const parts = (fullName || "").trim().split(/\s+/);
    return {
        firstName: parts[0] || "Customer",
        lastName: parts.slice(1).join(" ") || "",
    };
}

const CheckIcon = () => (
    <svg className="sp-checkIcon" viewBox="0 0 24 24" aria-hidden="true">
        <path
            d="M9.2 16.2 4.9 11.9a1 1 0 1 1 1.4-1.4l2.9 2.9 8.6-8.6a1 1 0 1 1 1.4 1.4l-10 10a1 1 0 0 1-1.4 0Z"
            fill="currentColor"
        />
    </svg>
);

const SubscriptionPage = () => {

    const userDetails = useSelector((state) => state?.user?.userDetails);
    const userId = useSelector((state) => state?.user?.userId);
    const isProUser = useSelector((state) => state?.user?.isPro);

    const dispatch = useDispatch();

    const [billing, setBilling] = useState("monthly"); // "monthly" | "yearly"
    const [authModalOpen, setAuthModalOpen] = useState(false);

    const [createOrder, {isLoading}] = useCreateOrderMutation();
    const selectedLanguage = useSelector((state) => state.language.language);
    const [language, setLanguage] = useState(sinhalaData[0]);

    useEffect(() => {
        switch (selectedLanguage.value) {
            case "english":
                setLanguage(englishData[0]);
                break;
            case "tamil":
                setLanguage(tamilData[0]);
                break;
            default:
                setLanguage(sinhalaData[0]);
                break;
        }
    }, [selectedLanguage]);

    const onAuth = async ({mode, email, password, name}) => {
        console.log({mode, email, password, name});

    };

    const plans = useMemo(() => {
        const raw = [
            {
                key: "pro",
                name: "Advanced",
                tagline: "Everything you need to master driving",
                price: {monthly: 199, yearly: 799},
                features: [
                    {text: "Complete Exam Knowledge", highlight: true},
                    {text: "500,000+ Student Success", highlight: true},
                    {text: "#1 on Android/iOS", highlight: false},
                    {text: "Thousands of Reviews", highlight: true},
                    {text: "Full Syllabus Coverage", highlight: true},
                    {text: "Past Papers Explained", highlight: false},
                    {text: "Topic Video Lessons", highlight: false},
                    {text: "Smart Progress Plan", highlight: false},
                ],
                popular: true,
            },
        ];

        return raw.map((p) => {
            const m = Number(p.price.monthly ?? 0);
            const y = Number(p.price.yearly ?? 0);
            const savings = Math.max(0, m * 12 - y);
            return {...p, savings};
        });
    }, []);

    const redirectToPayHere = (payment) => {
        const paymentUrl = payment.sandbox
            ? "https://sandbox.payhere.lk/pay/checkout"
            : "https://www.payhere.lk/pay/checkout";

        const allowed = [
            "merchant_id",
            "return_url",
            "cancel_url",
            "notify_url",
            "first_name",
            "last_name",
            "email",
            "phone",
            "address",
            "city",
            "country",
            "order_id",
            "items",
            "currency",
            "amount",
            "hash",
            "custom_1",
            "custom_2",
        ];

        const form = document.createElement("form");
        form.method = "POST";
        form.action = paymentUrl;

        allowed.forEach((k) => {
            if (payment[k] === undefined || payment[k] === null) return;
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = k;
            input.value = String(payment[k]);
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
    };

    const handlePayPro = async (plan) => {

        const token = localStorage.getItem("token");

        if (!token) {
            setAuthModalOpen(true);
            return;
        }

        const effectiveUserId = userDetails?.id || userId;
        if (!effectiveUserId) {
            Swal.fire({
                title: "Error",
                text: "User not found. Please sign in again.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        const amount = billing === "monthly" ? plan.price.monthly : plan.price.yearly;

        const { firstName, lastName } = splitName(userDetails.name);

        const obj = {
            userId: userDetails.id || userId,
            plan: "pro",
            amount: 199,
            currency: "LKR",
            items: "Pro Membership",
            customer: {
                first_name: firstName,
                last_name: lastName,
                email: userDetails.email,
                phone: userDetails.phoneNumber || "",
                address: userDetails.address || "",
                city: userDetails.city || "",
                country: "Sri Lanka",
            },
            token,
        };

        try {
            const result = await createOrder(obj).unwrap();
            toast.success("Order created. Redirecting to payment...");

            const payment = result?.data?.payment;

            if (payment?.order_id) {
                localStorage.setItem("payhere_last_order_id", payment.order_id);
            }

            redirectToPayHere(payment);
            return result;
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: "Failed to submit data. Please try again later.",
                icon: "error",
                confirmButtonText: "OK",
            });
            throw err;
        }
    };

    const handleChoosePlan = async (plan) => {
        if (plan.key === "free") {
            dispatch(downgradeToFree());
            toast.info("Switched to Free plan.");
            return;
        }

        if (isProUser) {
            Swal.fire({
                title: "You're already Pro",
                text: "Your account is already on the Pro plan.",
                icon: "info",
                confirmButtonText: "OK",
            });
            return;
        }

        await handlePayPro(plan);
    };

    return (
        <div className="sp-page">
            <div className="sp-container">
                {/* Header */}
                <div className="sp-header mt-5">

                    <h1 className="sp-title">
                        {language.title1}{" "}
                        <span className="sp-titleAccent">{language.title2}</span>
                    </h1>
                </div>

                {/* Plans */}
                <div
                    className="sp-grid"

                >
                    {plans.map((plan) => {
                        const currentPrice =
                            billing === "monthly" ? plan.price.monthly : plan.price.yearly;

                        const disabled = isLoading || (plan.key === "pro" && isProUser);

                        return (<div
                            key={plan.key}
                            className={`sp-cardWrap ${plan.popular ? "isPopular" : ""}`}
                          >
                            {plan.popular && (
                              <div className="sp-glow" aria-hidden="true" />
                            )}

                            <div className={`sp-card ${plan.popular ? "isPopular" : ""}`}>
                              {plan.popular && (
                                <div className="">
                                  {/* <SparkleIcon className="sp-bestValueIcon"/>
                                            PRO */}
                                </div>
                              )}

                              <div className="sp-cardBody">
                                <div className="sp-planHead">
                                  <span className="sp-price">
                                    {currentPrice}
                                  </span>
                                  <span className="sp-per">
                                    Rs /{" "}
                                    {billing === "monthly"
                                      ? "4 Months"
                                      : "year"}
                                  </span>
                                </div>

                                <div className="sp-priceBlock">
                                  <div className="sp-priceRow"></div>

                                  {billing === "yearly" && plan.savings > 0 && (
                                    <p className="sp-savings">
                                      Save {plan.savings} compared to monthly
                                      billing
                                    </p>
                                  )}
                                </div>

                                <button
                                  type="button"
                                  className={`sp-cta ${plan.popular ? "isPrimary" : "isDark"} ${
                                    disabled ? "isDisabled" : ""
                                  }`}
                                  // disabled={disabled}
                                  onClick={() => handleChoosePlan(plan)}
                                >
                                  {isLoading
                                    ? "Processing..."
                                    : plan.key === "pro" && isProUser
                                      ? "✓ You're Already Pro"
                                      : plan.key === "free"
                                        ? "Switch to Free →"
                                        : `${language.btnText} →`}
                                </button>

                                <div className="sp-simpleInfo">
                                  <h3 className="sp-simpleInfoTitle">
                                    {language.paymentTitle}
                                  </h3>

                                  {!!language.paymentSubtitle && (
                                    <p className="sp-simpleInfoSub">
                                      {language.paymentSubtitle}
                                    </p>
                                  )}

                                  <ul className="sp-simpleInfoList">
                                    {language.paymentPoints?.map(
                                      (text, idx) => (
                                        <li
                                          key={`pay-${idx}`}
                                          className="sp-simpleInfoItem"
                                        >
                                          {text}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>

                                <hr />

                                <div className="sp-features pt-2">
                                  <div className="sp-featuresGrid">
                                    {language.features.map((f, idx) => (
                                      <div
                                        key={`${plan.key}-${idx}`}
                                        className={`sp-feature ${f.highlight ? "isHighlight" : ""}`}
                                      >
                                        <div
                                          className={`sp-featureIcon ${
                                            f.highlight ? "isHighlight" : ""
                                          }`}
                                        >
                                          <CheckIcon />
                                        </div>
                                        <span className="sp-featureText">
                                          {f.text}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                <div className="sp-trust"></div>
                              </div>
                            </div>
                          </div>
                        );

                        
                    })}



                </div>

                {/* Social proof */}
                <div className="sp-social">
                   
                </div>

                {/* Auth modal */}
                <AuthModalRHF
                    open={authModalOpen}
                    onClose={() => setAuthModalOpen(false)}
                    onAuth={onAuth}
                    initialMode="signin"
                />
            </div>
        </div>
    );
};

export default SubscriptionPage;
